import { ChevronRight } from '@mui/icons-material';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { t } from 'i18next';
import { PATHS } from '../../../routing/routes';
import { Chip } from '../../components/Chip';
import { SEVERITY_COLORS, Severity } from '../../enums/Severity';
import { UrlHelper } from '../../utils/UrlHelper';
import { Issue } from '../models/Issue';
import { Slot } from '../models/Slot';

type Props = {
  data: Slot[];
  issue: Issue;
};

/**
 * Table to display Slots and their data.
 */
export const SlotsTable = ({ data, issue }: Props) => {
  return (
    <TableContainer component={Box}>
      <Table role="presentation" sx={{ '& th': { py: 1.5 }, '& td': { py: 1 } }}>
        <TableHead>
          <TableRow>
            <TableCell>{t('name')}</TableCell>
            <TableCell align="right">{t('instances')}:</TableCell>
            <TableCell align="right" sx={{ width: 50, px: 1.5 }}>
              <Chip
                color={SEVERITY_COLORS[Severity.HIGH].text}
                backgroundColor={SEVERITY_COLORS[Severity.HIGH].background}
                borderColor={SEVERITY_COLORS[Severity.HIGH].main}
              >
                {t('failed')}
              </Chip>
            </TableCell>
            <TableCell align="right" sx={{ width: 50, px: 1.5 }}>
              <Chip
                color={SEVERITY_COLORS[Severity.PASSED].text}
                backgroundColor={SEVERITY_COLORS[Severity.PASSED].background}
                borderColor={SEVERITY_COLORS[Severity.PASSED].main}
              >
                {t('passed')}
              </Chip>
            </TableCell>
            <TableCell align="right">{t('details')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((model) => {
            const reportId = model.getReportId() ?? '';
            const issueUrl = UrlHelper.generatePath(PATHS.REPORT_ISSUE, { reportId: reportId.toString(), issueId: issue.getNumber().toString() });
            // TODO(QCCT-557): Fix the vulnerability query and set the filter toggle query parameter only when necessary
            const issueLink = `${issueUrl}?filtered=true`;
            const ariaLabel = t('a11y.common.dashboard.navigateToIssueForReport', {
              issueNumber: issue.getNumber(),
              issueName: issue.getName(),
              reportId: model.getReportId(),
              reportName: model.getName(),
            });
            return (
              <TableRow key={model.getId()}>
                <TableCell colSpan={2}>{model.getName()}</TableCell>
                <TableCell align="right" sx={{ width: 50, px: 1.5 }}>
                  <Chip
                    color={SEVERITY_COLORS[Severity.HIGH].text}
                    backgroundColor={SEVERITY_COLORS[Severity.HIGH].background}
                    borderColor={SEVERITY_COLORS[Severity.HIGH].main}
                  >
                    {model.getFailed().format()}
                  </Chip>
                </TableCell>
                <TableCell align="right" sx={{ width: 50, px: 1.5 }}>
                  <Chip
                    color={SEVERITY_COLORS[Severity.PASSED].text}
                    backgroundColor={SEVERITY_COLORS[Severity.PASSED].background}
                    borderColor={SEVERITY_COLORS[Severity.PASSED].main}
                  >
                    {model.getPassed().format()}
                  </Chip>
                </TableCell>
                <TableCell align="right" sx={{ width: 160 }}>
                  {reportId && (
                    <Button variant="outlined" color="light-grey" href={issueLink} aria-label={ariaLabel}>
                      {t('details')}
                      <ChevronRight fontSize="inherit" sx={{ ml: 1 }} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
